import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';

interface HeaderProps {
  isHomePage?: boolean;
}

export default function Header({ isHomePage = false }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Products', href: '/products' },
    { name: 'Projects', href: '/projects' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const LogoContent = () => (
    <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
      PURECODE
    </span>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
      <nav className="mx-auto max-w-7xl px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center cursor-pointer" aria-label="PURECODE Ltd - Home">
              {isHomePage ? (
                <h1 className="m-0 p-0">
                  <LogoContent />
                </h1>
              ) : (
                <LogoContent />
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Home
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              About
            </Link>
            <Link to="/services" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Services
            </Link>
            <Link to="/products" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Products
            </Link>
            <Link to="/projects" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Projects
            </Link>
            <Link to="/case-studies" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Case Studies
            </Link>
            <Link to="/blog" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Blog
            </Link>
            <Link to="/careers" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">
              Careers
            </Link>
          </nav>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-teal-600 cursor-pointer"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="space-y-1">
              <Link
                to="/"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/') ? 'page' : undefined}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/about')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/about') ? 'page' : undefined}
              >
                About
              </Link>
              <Link
                to="/services"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/services')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/services') ? 'page' : undefined}
              >
                Services
              </Link>
              <Link
                to="/products"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/products')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/products') ? 'page' : undefined}
              >
                Products
              </Link>
              <Link
                to="/projects"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/projects')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/projects') ? 'page' : undefined}
              >
                Projects
              </Link>
              <Link
                to="/case-studies"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/case-studies')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/case-studies') ? 'page' : undefined}
              >
                Case Studies
              </Link>
              <Link
                to="/blog"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/blog')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/blog') ? 'page' : undefined}
              >
                Blog
              </Link>
              <Link
                to="/careers"
                className={`block px-3 py-2 rounded-md text-base font-medium cursor-pointer ${
                  isActive('/careers')
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/careers') ? 'page' : undefined}
              >
                Careers
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
